import { useLocalObservable } from "mobx-react-lite";
import { makeAutoObservable } from "mobx";
import {
  getLeadLabel,
  normalizeToE164,
} from "apps/crm-portal/src/app/utils/phone";
import { LEAD_OBJECT_TYPE } from "../components/leads/constants/constants";
import { useEffect } from "react";

export type DialRecord = {
  cadenceContactInfo?: {
    leadId?: any;
    contactId?: any;
    cadencesContactDataId?: any;
    doNotCall?: boolean;
    phone?: any;
    entityId?: any;
  };
  recordType: string;
  name?: any;
  phoneNumber?: any;
};

export type LeadOverride = {
  name?: any;
  rawPhone?: any;
  e164?: any;
  doNotCall?: boolean;
  recordType?: any;
};

export class DialSessionStore {
  records: DialRecord[] = [];
  selectedRecordId: string | null = null;
  leadOverrides: Record<string, LeadOverride> = {};
  nextCandidateId: string | null = null;
  autoModalOpen = false;

  selectedPerson: DialRecord | null = null;
  activeCallId: string | null = null;
  callStatus: "idle" | "connecting" | "connected" | "ended" | "failed" = "idle";

  confirmModalOpen = false;
  completedRecordIds: Set<string> = new Set();
  skippedRecordIds: Set<string> = new Set();


  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  resetCompletedRecordIds() {
    this.completedRecordIds= new Set();
    this.skippedRecordIds = new Set();
  }

  resetSkippedRecordIds() {
    this.skippedRecordIds = new Set();
  }

  setRecords(records) {
    this.records = records ?? [];
  }

  setSelectedRecord(id) {
    this.selectedRecordId = id;
  }

  setOverride(id, override) {
    this.leadOverrides[id] = override;
  }

  resetSelectedPerson() {
    this.selectedPerson = null;
    this.setSelectedRecord(null);
  }

  selectPerson(person) {
    this.selectedPerson = person;
    const id = this.getId(person);
    this.setSelectedRecord(id);
    this.closeAutoModal();
  }

  setActiveCall(id) {
    this.activeCallId = id;
  }

  setCallStatus(status) {
    this.callStatus = status as any;
  }

  getId(record?){
    if (!record) return null;
    return (
      record?.cadenceContactInfo?.leadId ||
      record?.cadenceContactInfo?.contactId ||
      null
    );
  }

  getContactId(record?){
    if (!record) return null;
    return (
      record?.cadenceContactInfo?.contactId ||
      null
    );
  }

  geLeadtId(record?){
    if (!record) return null;
    return (
      record?.cadenceContactInfo?.leadId ||
      null
    );
  }

  getDoNotCall(id?){
    if (!id) return false;
    const override = this.leadOverrides[id];
    if (override?.doNotCall !== undefined) return override.doNotCall;
    const record = this.records.find(
      (r) => String(this.getId(r)) === String(id),
    );
    return !!record?.cadenceContactInfo?.doNotCall;
  }

  getName(id?){
    if (!id) return "";
    const override = this.leadOverrides[id];
    if (override?.name) return override.name;
    const record = this.records.find(
      (r) => String(this.getId(r)) === String(id),
    );
    if (!record) return "";
    switch (record.recordType?.toUpperCase()) {
      case LEAD_OBJECT_TYPE:
        return getLeadLabel([], id) ?? record.name ?? "";
      default:
        return record.name ?? "";
    }
  }

  getE164(id?){
    if (!id) return "";
    const override = this.leadOverrides[id];
    if (override?.e164) return override.e164;
    const record = this.records.find(
      (r) => String(this.getId(r)) === String(id),
    );
    if (!record) return "";
    const raw = record?.cadenceContactInfo?.phone || record?.phoneNumber || "";
    return normalizeToE164(String(raw ?? ""));
  }

  findNextDialable() {
    if (!this.records.length || !this.selectedRecordId) return null;
    const startIdx = this.records.findIndex(
      (r) => String(this.getId(r)) === String(this.selectedRecordId)
    );
    for (let i = startIdx + 1; i < this.records.length; i++) {
      const record = this.records[i];
      const cadenceId = record?.cadenceContactInfo?.cadencesContactDataId;
      const id = this.getId(record);
      const phone = this.getE164(id);
      const dnc = this.getDoNotCall(id);
      const skipped = this.skippedRecordIds.has(String(cadenceId));
      if (phone && !dnc && !skipped) return record;
    }
    return null;
  }

  openAutoModal(nextId: string) {
    this.nextCandidateId = nextId;
    this.autoModalOpen = true;
  }

  closeAutoModal() {
    this.nextCandidateId = null;
    this.autoModalOpen = false;
  }

  openConfirmModal() {
    this.confirmModalOpen = true;
  }

  closeConfirmModal() {
    this.confirmModalOpen = false;
  }

  dialCurrent(twilio: any, userId?: string | number) {
    const id = this.selectedRecordId;
    if (!id) return;

    const record =
      this.selectedPerson ||
      this.records.find((r) => String(this.getId(r)) === String(id));

    const contactId = this.getContactId(record);
    const e164 = this.getE164(id);
    const leadId = this.geLeadtId(record);

    if (e164) {
      const params: any = {};

      if (userId != null) {
        params.userId = Number(userId);
      }

      if (leadId != null) {
        params.leadId = Number(leadId);
      }

      if (contactId != null) {
        params.contactId = Number(contactId);
      }

      twilio.connect(e164, params);
      this.setActiveCall(id);
      this.setCallStatus("connecting");
    }
  }

  dialNext(twilio: any, userId?: string | number) {
    const nextRecord = this.findNextDialable();
    if (!nextRecord) return;

    if (twilio.hasActiveCall) {
      try {
        twilio.hangup();
      } catch (e) {
        console.warn("Error hanging up previous call:", e);
      }
    }

    const id = this.getId(nextRecord);
    if (!id) return;

    const leadId = this.geLeadtId(nextRecord);
    const contactId = this.getContactId(nextRecord);

    this.setSelectedRecord(id);
    this.selectedPerson = nextRecord;
    const e164 = this.getE164(id);

    if (e164) {
      const params: any = {};

      if (userId != null) {
        params.userId = Number(userId);
      }

      if (leadId != null) {
        params.leadId = Number(leadId);
      }

      if (contactId != null) {
        params.contactId = Number(contactId);
      }

      twilio.connect(e164, params);
      this.setActiveCall(id);
      this.setCallStatus("connecting");
    }
  }

  handleAutoTimeout(twilio: any, userId?: string | number) {
    if (!this.nextCandidateId) return;

    const id = this.nextCandidateId;
    const e164 = this.getE164(id);
    if (!e164) return;

    this.setSelectedRecord(id);
    const record = this.records.find((r) => this.getId(r) === id);
    this.selectedPerson = record || null;

    const leadId = this.geLeadtId(record);
    const contactId = this.getContactId(record);

    const params: any = {};

    if (userId != null) {
      params.userId = Number(userId);
    }

    if (leadId != null) {
      params.leadId = Number(leadId);
    }

    if (contactId != null) {
      params.contactId = Number(contactId);
    }

    twilio.connect(e164, params);
    this.closeAutoModal();
    this.setActiveCall(id);
    this.setCallStatus("connecting");
  }

  async handleCallEnd(twilioStatus: string, cadenceStore: any) {
    const ended = ["ended", "failed"].includes(twilioStatus);
    const currentId = this.getId(this.selectedPerson);
    if (this.callStatus === "connected" && ended && this.selectedPerson) {
        this.completedRecordIds.add(String(currentId));
        await cadenceStore.completeCallStepForCadenceContact(this.selectedPerson?.cadenceContactInfo?.cadencesContactDataId);
        const next = this.findNextDialable();
        if (next) {
          const nextId = this.getId(next);
          if (nextId) {
            this.openAutoModal(nextId);
          }
        }
    }
    this.setCallStatus(twilioStatus);
  }

  isCompleted(id?){
    if (!id) return false;
    return this.completedRecordIds.has(String(id));
  }

  markAsSkipped(id?: string | string[]) {
    if (!id) return;
    if (Array.isArray(id)) {
      id.forEach((x) => this.skippedRecordIds.add(String(x)));
    } else {
      this.skippedRecordIds.add(String(id));
    }
  }

  isSkipped(id?: string) {
    if (!id) return false;
    return this.skippedRecordIds.has(String(id));
  }

}



export const useDialSession = (records: DialRecord[]) => {
  const store = useLocalObservable(() => new DialSessionStore());
  useEffect(() => {
    store.setRecords(records ?? []);
  }, [records, store]);
  return store;
};
